import React, { useEffect, useRef, useMemo } from 'react';
import { DateTime } from "luxon";

const GenericList = ({
    data = [],
    columns = [],
    onRowClick,
    searchTerm = '',
    searchKeyType = '',
    emptyText = '',
    onLoadMore,
    hasMore,
    loading,
    enableSelection = false,
    selectedIds = [],
    onSelectionChange,
    rowIdKey = 'id'
}) => {
    const bottomRef = useRef(null);

    const processedData = useMemo(() => {
        const validData = Array.isArray(data) ? data : [];
        if (!searchTerm) return validData;
        // פונקציה שמנקה תווים מיוחדים
        const normalize = str => (str || '').toString().replace(/[^\w\u0590-\u05FF\s]/g, '').toLowerCase();
        const normTerm = normalize(searchTerm);
        return validData.filter(item => {
            let value = '';
            switch (searchKeyType) {
                case 'beneficiary_name':
                    value = `${item.beneficiary_first_name || ''} ${item.beneficiary_last_name || ''}`;
                    break;
                case 'applicant_name':
                    value = `${item.applicant_first_name || ''} ${item.applicant_last_name || ''}`;
                    break;
                case 'admin_comments':
                    value = item.admin_comments || '';
                    break;
                case 'status':
                    if (item.status === 'Approved') value = 'מאושר';
                    else if (item.status === 'Rejected') value = 'נדחה';
                    else if (item.status === 'Pending') value = 'ממתין';
                    else value = item.status || '';
                    break;
                case 'submission_date':
                    value = item.submission_date 
                        ? DateTime.fromJSDate(new Date(item.submission_date)).toFormat('dd.MM.yyyy')
                        : '';
                    break;
                default:
                    value = (item[searchKeyType] || '').toString();
            }
            return normalize(value).includes(normTerm);
        });
    }, [data, searchTerm, searchKeyType]);

    const selectedIdsSet = useMemo(
        () => new Set((selectedIds || []).map((id) => String(id))),
        [selectedIds]
    );

    const visibleIds = useMemo(
        () => processedData
            .map((item) => item?.[rowIdKey])
            .filter((id) => id !== undefined && id !== null)
            .map((id) => String(id)),
        [processedData, rowIdKey]
    );

    const allVisibleSelected =
        enableSelection && visibleIds.length > 0 && visibleIds.every((id) => selectedIdsSet.has(id));

    const someVisibleSelected =
        enableSelection && visibleIds.some((id) => selectedIdsSet.has(id)) && !allVisibleSelected;

    const toggleSingleSelection = (event, rowId) => {
        event.stopPropagation();
        if (!onSelectionChange || rowId === undefined || rowId === null) {
            return;
        }

        const rowIdStr = String(rowId);
        const nextSelection = new Set(selectedIdsSet);
        if (nextSelection.has(rowIdStr)) {
            nextSelection.delete(rowIdStr);
        } else {
            nextSelection.add(rowIdStr);
        }
        onSelectionChange(Array.from(nextSelection));
    };

    const toggleVisibleSelection = (event) => {
        event.stopPropagation();
        if (!onSelectionChange) {
            return;
        }

        const nextSelection = new Set(selectedIdsSet);
        if (event.target.checked) {
            visibleIds.forEach((id) => nextSelection.add(id));
        } else {
            visibleIds.forEach((id) => nextSelection.delete(id));
        }
        onSelectionChange(Array.from(nextSelection));
    };

    useEffect(() => {
        if (!hasMore || loading)
            return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    onLoadMore && onLoadMore();
                }
            },
            { threshold: 1 }
        );
        if (bottomRef.current) {
            observer.observe(bottomRef.current);
        }
        return () => observer.disconnect();
    }, [hasMore, loading, onLoadMore]);

    return (
        <div className="generic-list">
            {processedData.length === 0 && !loading ? (
                <div style={{ textAlign: 'center', margin: 16, color: '#888' }}>
                    {emptyText || 'לא נמצאו פריטים'}
                </div>
            ) : (
                <table className="transactions-table">
                    <thead>
                        <tr>
                            {enableSelection && (
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        ref={(input) => {
                                            if (input) {
                                                input.indeterminate = someVisibleSelected;
                                            }
                                        }}
                                        onChange={toggleVisibleSelection}
                                        aria-label="בחר הכל"
                                    />
                                </th>
                            )}
                            {columns.map((col) => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {processedData.map((item, index) => (
                            <tr key={`${item.id || index}-${index}`} onClick={() => onRowClick && onRowClick(item)}>

                                {enableSelection && (
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIdsSet.has(String(item?.[rowIdKey]))}
                                            onChange={(event) => toggleSingleSelection(event, item?.[rowIdKey])}
                                            onClick={(event) => event.stopPropagation()}
                                            aria-label={`בחר ${item?.[rowIdKey] || ''}`}
                                        />
                                    </td>
                                )}

                                {columns.map((col) => (
                                    <td key={col.key}>
                                        {col.render ? col.render(item) : (item[col.key] || '-')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div ref={bottomRef} style={{ height: 1 }} />
            {loading && <div style={{ textAlign: 'center', margin: 8 }}>טוען...</div>}
        </div>
    );
};
export default GenericList;

